import { useState, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  UserIcon,
  Settings01Icon as Settings,
  Logout03Icon as LogOut,
  DashboardSpeed02Icon as Dashboard,
  CustomerSupportIcon as LifeBuoy,
  Camera01Icon as Camera,
  AudioWave01Icon as AudioLines,
  FavouriteIcon as Heart,
  Ticket01Icon as Ticket,
  CardExchange02Icon as CardExchange,
} from "@hugeicons-pro/core-stroke-rounded";
import { GiftCardIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { User as UserType } from "@supabase/supabase-js";
import { Link } from "react-router-dom";
import { hapticFeedback } from "@/utils/mobileInteractions";
import { useOptimizedProfileData } from "@/hooks/profile/useOptimizedProfileData";
import { useDailyStreamLimits } from "@/hooks/useDailyStreamLimits";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOverlayTrigger } from "@/hooks/useOverlayTrigger";
interface MobileProfileSheetProps {
  user: UserType;
  userRole: string | null;
  onSignOut: () => void;
  getDashboardLink: () => string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}
export function MobileProfileSheet({
  user,
  userRole,
  onSignOut,
  getDashboardLink,
  open,
  onOpenChange,
  hideTrigger,
}: MobileProfileSheetProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined && onOpenChange !== undefined;
  const isProfileSheetOpen = isControlled ? open! : internalOpen;
  const setIsProfileSheetOpen = (v: boolean) => {
    if (isControlled) onOpenChange!(v);
    else setInternalOpen(v);
  };
  useOverlayTrigger(isProfileSheetOpen);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { profile } = useOptimizedProfileData();
  const {
    streamsUsed,
    streamsRemaining,
    isLoading: isLoadingStreams,
  } = useDailyStreamLimits();

  const handleSignOut = async () => {
    await onSignOut();
    setIsProfileSheetOpen(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Upload to Supabase storage
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from("public_profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Profile picture updated successfully",
      });

      // Reload the page to refresh profile data
      window.location.reload();
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload profile picture. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Use profile avatar_url with fallback to user metadata
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url;
  const displayName =
    profile?.display_name ||
    profile?.name ||
    user?.user_metadata?.full_name ||
    user?.email;
  return (
    <Sheet open={isProfileSheetOpen} onOpenChange={setIsProfileSheetOpen}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <button
            onClick={() => hapticFeedback.light()}
            className="flex items-center justify-center p-2 ml-auto mr-4 rounded-xl transition-all duration-200 active:scale-95 touch-manipulation"
          >
            <Avatar className="h-8 w-8 ring-1 ring-white/20">
              <AvatarImage src={avatarUrl} alt="Profile" />
              <AvatarFallback className=" text-white text-sm">
                {displayName?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </button>
        </SheetTrigger>
      )}
      <SheetContent
        side="bottom"
        overlayClassName="z-[68]"
        className="z-[68] bg-black/95 backdrop-blur-lg border-white/10 h-[90vh] max-h-[90vh] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-2 shrink-0">
          <SheetTitle className="text-white">Profile Menu</SheetTitle>
        </SheetHeader>
        <div className="mt-2 space-y-4 overflow-y-auto px-6 pb-[180px] flex-1">
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
            <div className="flex items-center space-x-3">
              <div
                className="relative group cursor-pointer"
                onClick={handleAvatarClick}
              >
                <Avatar className="h-12 w-12">
                  <AvatarImage src={avatarUrl} alt="Profile" />
                  <AvatarFallback>
                    <HugeiconsIcon icon={UserIcon} size={20} />
                  </AvatarFallback>
                </Avatar>
                <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {isUploading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <HugeiconsIcon icon={Camera} size={16} className="text-white" />
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <div>
                <Link
                  to={`/profiles/${profile?.username || user.id}`}
                  onClick={() => setIsProfileSheetOpen(false)}
                >
                  <p className="text-white text-lg font-medium hover:text-primary-light transition-colors cursor-pointer">
                    {displayName}
                  </p>
                </Link>
              </div>
            </div>
            <Link to="/profile" onClick={() => setIsProfileSheetOpen(false)}>
              <button className="p-2 rounded-lg transition-colors">
                <HugeiconsIcon
                  icon={Settings}
                  size={18}
                  className="text-gray-400 hover:text-white"
                />
              </button>
            </Link>
          </div>

          {/* Daily Stream Usage - Fans Only */}
          {userRole === "fan" && !isLoadingStreams && (
            <div className="p-3 rounded-lg bg-white/5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                <HugeiconsIcon icon={AudioLines} className="h-3 w-3 text-primary" />
                <span>{streamsUsed}/50 streams</span>
                <span className="ml-auto">{streamsRemaining} left</span>
              </div>
              <Progress value={(streamsUsed / 50) * 100} className="h-1.5" />
            </div>
          )}

          <div className="space-y-2">
            <Link
              to={getDashboardLink()}
              onClick={() => setIsProfileSheetOpen(false)}
            >
              <Button
                variant="ghost"
                className="w-full justify-start text-lg text-white hover:text-primary-light hover:bg-white/5"
              >
                <HugeiconsIcon icon={Dashboard} size={18} className="mr-3" />
                Dashboard
              </Button>
            </Link>

            {userRole === "fan" && (
              <>
                <Separator className="my-2 bg-white/10" />
                {[
                  {
                    to: "/dashboard/fan?tab=favorites",
                    icon: Heart,
                    label: "Favorites",
                  },
                  {
                    to: "/dashboard/fan?tab=history",
                    icon: AudioLines,
                    label: "Listening History",
                  },
                  {
                    to: "/dashboard/fan?tab=tickets",
                    icon: Ticket,
                    label: "My Tickets",
                  },
                  {
                    to: "/dashboard/fan?tab=gifting",
                    icon: GiftCardIcon,
                    label: "Gifting",
                  },
                  {
                    to: "/dashboard/fan?tab=transactions",
                    icon: CardExchange,
                    label: "Transactions",
                  },
                ].map(({ to, icon: Icon, label }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsProfileSheetOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-lg text-white hover:text-primary-light hover:bg-white/5"
                    >
                      <HugeiconsIcon icon={Icon} size={18} className="mr-3" />
                      {label}
                    </Button>
                  </Link>
                ))}
                <Separator className="my-2 bg-white/10" />
              </>
            )}

            <Link to="/support" onClick={() => setIsProfileSheetOpen(false)}>
              <Button
                variant="ghost"
                className="w-full justify-start text-lg text-white hover:text-primary-light hover:bg-white/5"
              >
                <HugeiconsIcon icon={LifeBuoy} size={18} className="mr-3" />
                Support
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={handleSignOut}
              className="w-full justify-start text-lg text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <HugeiconsIcon icon={LogOut} size={18} className="mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
